/**
 * TavernFloor — top-down social view of the room. Per
 * docs/ui/tavern-floor-chat.md, this is now the SOCIAL HEART of the
 * waiting state: walkers show readiness/host/disconnected status,
 * chat messages render as speech bubbles above the speaker, reactions
 * pop as single-glyph bubbles, and the chat composer docks to the
 * bottom of the floor view so it's always reachable on mobile (no
 * scroll required).
 *
 * Position updates run on rAF and mutate DOM directly via refs (no
 * React re-render per frame, no CSS transition fight). Bubble state
 * is React-driven because they spawn / despawn at lower cadence.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { LobbyView } from '@tt/lobby';
import { IconAvatar } from '../characters/IconAvatar';
import { titleRarityClass } from '../me/titleRarity';
import type { ChatMsg, Reaction } from './useLobbySocket';

type Player = LobbyView['players'][number];

interface Spot {
  id: string;
  label: string;
  x: number; // % across the floor
  y: number; // % down the floor
  emoji: string;
}

const SPOTS: Spot[] = [
  { id: 'bar',       label: 'the bar',    x: 16, y: 30, emoji: '🍻' },
  { id: 'hearth',    label: 'the hearth', x: 50, y: 22, emoji: '🔥' },
  { id: 'window',    label: 'the window', x: 84, y: 30, emoji: '🌙' },
  { id: 'billiards', label: 'the table',  x: 32, y: 70, emoji: '🎯' },
  { id: 'corner',    label: 'the corner', x: 70, y: 75, emoji: '🪑' },
];

interface AvatarSim {
  player: Player;
  x: number; y: number;
  targetX: number; targetY: number;
  dwell: number;
  hash: number;
  facingFlip: 1 | -1;
}

interface Bubble {
  /** Unique key for React. */
  id: string;
  /** Owner — the walker the bubble floats above. */
  charId: string;
  kind: 'chat' | 'reaction';
  text: string;
  /** ms epoch when the bubble should disappear. */
  until: number;
}

const CHAT_BUBBLE_MS = 6000;
const REACTION_BUBBLE_MS = 2200;

interface Props {
  players: Player[];
  /** Chat messages from useLobbySocket — bubbles spawn for new entries. */
  chat?: ChatMsg[];
  /** Reactions from useLobbySocket — single-glyph bubbles. */
  reactions?: Reaction[];
  /** Wired to sock.sendChat — composer pill submit handler. */
  onSendChat?: (text: string) => void;
  /** Wired to sock.sendReaction — emoji picker. */
  onSendReaction?: (emoji: string) => void;
  /** Reaction-picker emoji set, per the lobby's unlocked list. */
  reactionEmojis?: string[];
  /** When false, hide the composer (chat disabled in this lobby). */
  chatEnabled?: boolean;
  onInspectPlayer?: (charId: string) => void;
  /** Local player's charId — shown with a "me" rim. */
  meCharId?: string;
}

export function TavernFloor({
  players, chat, reactions, onSendChat, onSendReaction,
  reactionEmojis, chatEnabled = true, onInspectPlayer, meCharId,
}: Props) {
  // Stable per-player simulation state. Re-seeded only when the player
  // roster changes (join / leave).
  const sims = useMemo<AvatarSim[]>(() => players.map((p) => {
    const h = hashId(p.charId);
    const spot = SPOTS[h % SPOTS.length]!;
    return {
      player: p,
      x: spot.x, y: spot.y,
      targetX: spot.x, targetY: spot.y,
      dwell: 4 + (h % 5),
      hash: h,
      facingFlip: 1,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [players.length, players.map((p) => p.charId).join(',')]);

  // DOM refs — one per walker. Position is mutated each frame in rAF
  // without a React re-render. Inner-icon ref is the one that gets the
  // facing flip — the outer button stays unflipped so the nameplate
  // never reads backwards (per docs/ui/tavern-floor-chat.md §3.1).
  const walkerRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const iconRefs   = useRef(new Map<string, HTMLSpanElement | null>());

  // Speech bubbles + reaction bubbles. Keyed by bubble.id so multiple
  // can stack per walker. Bubbles auto-expire by `until`.
  const [bubbles, setBubbles] = useState<Bubble[]>([]);

  // Track which chat / reaction items we've already turned into
  // bubbles so reconnects / re-renders don't spawn duplicates.
  const lastChatKeyRef = useRef<string>('');
  const lastReactionIdRef = useRef<number>(0);
  // Per-charId timestamp of the LAST bubble we spawned, to rate-limit
  // bubble spawns to one per 3s per walker. Without this, rapid
  // chat/reaction bursts produce stacked bubbles that visually fight
  // for space and trigger the wrap-into-3-letter-rows bug.
  const lastBubbleAtRef = useRef<Map<string, number>>(new Map());
  const BUBBLE_RATE_LIMIT_MS = 3000;

  // Composer state.
  const [draft, setDraft] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Spawn bubbles for new chat messages.
  useEffect(() => {
    if (!chat || chat.length === 0) return;
    const newest = chat[chat.length - 1]!;
    const key = `${newest.at}-${newest.charId}-${newest.text.slice(0, 16)}`;
    if (key === lastChatKeyRef.current) return;
    lastChatKeyRef.current = key;
    // Rate-limit per-walker so spammy chat bursts don't stack 3+ bubbles
    // that visually compete and break their layout.
    const lastAt = lastBubbleAtRef.current.get(newest.charId) ?? 0;
    if (Date.now() - lastAt < BUBBLE_RATE_LIMIT_MS) return;
    lastBubbleAtRef.current.set(newest.charId, Date.now());
    const id = `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setBubbles((prev) => [
      ...prev.filter((b) => b.until > Date.now()),
      { id, charId: newest.charId, kind: 'chat', text: newest.text.slice(0, 80), until: Date.now() + CHAT_BUBBLE_MS },
    ]);
  }, [chat]);

  // Spawn bubbles for new reactions.
  useEffect(() => {
    if (!reactions || reactions.length === 0) return;
    const newest = reactions[reactions.length - 1]!;
    if (newest.id === lastReactionIdRef.current) return;
    lastReactionIdRef.current = newest.id;
    const lastAt = lastBubbleAtRef.current.get(newest.charId) ?? 0;
    if (Date.now() - lastAt < BUBBLE_RATE_LIMIT_MS) return;
    lastBubbleAtRef.current.set(newest.charId, Date.now());
    const id = `r-${newest.id}`;
    setBubbles((prev) => [
      ...prev.filter((b) => b.until > Date.now()),
      { id, charId: newest.charId, kind: 'reaction', text: newest.emoji, until: Date.now() + REACTION_BUBBLE_MS },
    ]);
  }, [reactions]);

  // Periodically GC expired bubbles.
  useEffect(() => {
    if (bubbles.length === 0) return;
    const t = window.setTimeout(() => {
      setBubbles((prev) => prev.filter((b) => b.until > Date.now()));
    }, 600);
    return () => window.clearTimeout(t);
  }, [bubbles]);

  // rAF walker loop.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(50, now - last) / 1000;
      last = now;
      for (const a of sims) {
        const k = Math.min(1, dt * 0.6);
        a.x += (a.targetX - a.x) * k;
        a.y += (a.targetY - a.y) * k;
        const dx = a.targetX - a.x;
        const newFlip: 1 | -1 = Math.abs(dx) > 0.5 ? (dx < 0 ? -1 : 1) : a.facingFlip;
        a.facingFlip = newFlip;
        a.dwell -= dt;
        if (a.dwell <= 0) {
          a.hash = (a.hash * 1103515245 + 12345) & 0x7fffffff;
          const spot = SPOTS[a.hash % SPOTS.length]!;
          a.targetX = spot.x + ((a.hash >> 4) % 8) - 4;
          a.targetY = spot.y + ((a.hash >> 8) % 6) - 3;
          a.dwell = 5 + (a.hash % 7);
        }
        // Drive DOM directly. The OUTER button moves but never flips;
        // the INNER icon span owns the scaleX flip so the nameplate
        // text doesn't render backwards when walking left.
        const el = walkerRefs.current.get(a.player.charId);
        if (el) {
          el.style.left = `${a.x}%`;
          el.style.top = `${a.y}%`;
        }
        const iconEl = iconRefs.current.get(a.player.charId);
        if (iconEl) {
          iconEl.style.transform = `scaleX(${newFlip})`;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sims]);

  if (players.length === 0) return null;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t || !onSendChat) return;
    onSendChat(t);
    setDraft('');
  };

  const reactionPicks = (reactionEmojis && reactionEmojis.length > 0)
    ? reactionEmojis
    : ['👏', '🔥', '💀', '🎉', '😂', '😱'];

  return (
    <section className="tavern-floor" aria-label="players in the tavern">
      <div className="tavern-floor-board">
        {SPOTS.map((s) => (
          <span
            key={s.id}
            className="tavern-floor-spot"
            style={{ left: `${s.x}%`, top: `${s.y}%` }}
            title={s.label}
          >
            <span aria-hidden="true">{s.emoji}</span>
          </span>
        ))}
        {sims.map((a) => {
          const myBubbles = bubbles.filter((b) => b.charId === a.player.charId).slice(-3);
          const isMe = meCharId === a.player.charId;
          const isHost = !!a.player.isHost;
          const isReady = !!a.player.ready;
          const isDisco = !!a.player.isDisconnected;
          return (
            <button
              type="button"
              key={a.player.charId}
              ref={(el) => { walkerRefs.current.set(a.player.charId, el); }}
              className={`tavern-floor-walker${isMe ? ' is-me' : ''}${isReady ? ' is-ready' : ''}${isHost ? ' is-host' : ''}${isDisco ? ' is-disco' : ''}`}
              style={{
                left: `${a.x}%`,
                top: `${a.y}%`,
              }}
              onClick={() => onInspectPlayer?.(a.player.charId)}
              aria-label={`${a.player.name}${isReady ? ' (ready)' : ''}${isHost ? ' (host)' : ''}${isDisco ? ' (disconnected)' : ''}`}
            >
              {/* Speech / reaction bubbles, stacked newest-up. */}
              {myBubbles.length > 0 && (
                <span className="tavern-floor-bubble-stack" aria-hidden="true">
                  {myBubbles.map((b, i) => (
                    <span
                      key={b.id}
                      className={`tavern-floor-bubble tavern-floor-bubble-${b.kind}`}
                      style={{ ['--stack-i' as string]: String(myBubbles.length - 1 - i) }}
                    >
                      {b.text}
                    </span>
                  ))}
                </span>
              )}
              {/* Inner icon — this is what flips when walking left. */}
              <span
                className="tavern-floor-walker-icon"
                ref={(el) => { iconRefs.current.set(a.player.charId, el); }}
              >
                <IconAvatar icon={a.player.icon} size={48} alt="" />
                {isHost && <span className="tavern-floor-crown" aria-hidden="true">♛</span>}
                {isReady && <span className="tavern-floor-tick" aria-hidden="true">✓</span>}
              </span>
              <span className="tavern-floor-name">
                <strong>{a.player.name}</strong>
                {a.player.title && a.player.title !== 'Tavern Regular' && (
                  <small className={titleRarityClass(a.player.title)}>{a.player.title}</small>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Composer pill docked to the bottom of the floor.
          Always visible — no scroll required on mobile. */}
      {chatEnabled && (
        <form className={`tavern-floor-composer${historyOpen ? ' history-open' : ''}`} onSubmit={onSubmit}>
          <button
            type="button"
            className="tavern-floor-composer-history-toggle"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'}
          >
            {historyOpen ? '▼' : '▲'}
          </button>
          {historyOpen && chat && chat.length > 0 && (
            <div className="tavern-floor-history" role="log" aria-live="polite">
              {chat.slice(-12).map((m, i) => (
                <div key={i} className="tavern-floor-history-row">
                  <span className="tavern-floor-history-name">{m.name}:</span> <span>{m.text}</span>
                </div>
              ))}
            </div>
          )}
          <input
            type="text"
            className="tavern-floor-composer-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="say something…"
            maxLength={200}
            aria-label="Chat message"
          />
          <button
            type="button"
            className="tavern-floor-composer-react"
            onClick={() => setPickerOpen((v) => !v)}
            aria-label="Send a reaction"
            aria-expanded={pickerOpen}
          >
            ☺
          </button>
          {pickerOpen && (
            <div className="tavern-floor-react-picker" role="dialog" aria-label="Reactions">
              {reactionPicks.map((e) => (
                <button
                  key={e}
                  type="button"
                  className="tavern-floor-react-glyph"
                  onClick={() => { onSendReaction?.(e); setPickerOpen(false); }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
          <button
            type="submit"
            className="tavern-floor-composer-send"
            disabled={!draft.trim()}
            aria-label="Send"
          >
            <span className="tavern-floor-composer-seal">✉</span>
          </button>
        </form>
      )}
      <div className="tavern-floor-tagline">at the tavern · tap a regular to peek</div>
    </section>
  );
}

function hashId(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
}
